import { z } from 'zod';

import type { Role } from './enums.js';

/**
 * The admin dashboard (team overview, pending invites, the week ahead):
 * the first screen in this product that deliberately aggregates across
 * every team a person holds standing on, rather than the one team a
 * request acts under.
 */

/**
 * Who may open it: Owner, Head Coach and Assistant Coach.
 *
 * BUILD_PLAN section 3 gives Owner "everything" and Head Coach "everything
 * except delete team and transfer ownership" on a single team. This screen
 * is read access across every team at once, which is a wider surface than
 * any single-team grant in that table, so it is held to the narrowest rows
 * rather than opened to every role with roster read. Team Manager already
 * holds a cross-team-shaped grant nowhere else in the product; this does not
 * add one for it.
 *
 * Assistant Coach is in since ADR-0108 (owner decisions of 22 and 24 October
 * 2026: head and assistant coach hold one permission set). Plan slice 5 of
 * #68 narrows this to the Owner alone, which removes both coaches together.
 *
 * Must stay a subset of `RSVP_ROLLUP`: the dashboard reads each team's
 * roll-up (`admin-service.ts` asserts it).
 */
export const ADMIN_DASHBOARD_ACCESS: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
]);

export function canViewAdminDashboard(role: Role): boolean {
  return ADMIN_DASHBOARD_ACCESS.has(role);
}

/**
 * The three flows that already mint a single-use token and show its link
 * once, unified into one list. Named in plain language here so the server
 * and the client agree on the words a coach reads, rather than each
 * inventing its own phrasing for `auth_tokens.type`.
 */
export const PendingInviteKindSchema = z.enum(['guardian_invite', 'team_invite', 'coach_setup']);
export type PendingInviteKind = z.infer<typeof PendingInviteKindSchema>;

const PENDING_INVITE_KIND_LABEL: Readonly<Record<PendingInviteKind, string>> = {
  guardian_invite: 'Guardian invite',
  team_invite: 'New team invite',
  coach_setup: 'Coach setup link',
};

export function pendingInviteKindLabel(kind: PendingInviteKind): string {
  return PENDING_INVITE_KIND_LABEL[kind];
}
